import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { PulseLoader, ClipLoader } from "react-spinners";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "items-center gap-3 justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary:
          "bg-action-fill-primary-default text-action-ink-on-primary-normal hover:bg-action-fill-primary-hover",
        secondary: "border",
        tertiary: "hover:bg-accent hover:text-accent-foreground",
      },
      color: {
        primary: "",
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
        white: "",
      },
      size: {
        xsmall: "md:h-[28px] px-3 rounded-medium text-body-small-medium",
        small: "md:h-[32px] px-4 rounded-medium text-body-small-medium",
        medium: "md:h-[36px] px-5 py-2 rounded-large text-body-medium-medium",
        large: "md:h-[44px] px-6 rounded-large text-body-large-medium",
      },
      isIconOnly: {
        true: "aspect-square p-0",
        false: "",
      },
      isLoading: {
        true: "cursor-not-allowed",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
      isFullWidth: {
        true: "flex w-full",
        false: "flex w-fit",
      },
    },
    compoundVariants: [
      // Primary variant colors
      {
        variant: "primary",
        color: "primary",
        class: `bg-action-fill-primary-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-primary-hover 
          disabled:bg-action-fill-primary-disabled
          disabled:text-action-ink-primary-disabled,
          active:bg-action-fill-primary-activated
          `,
      },
      {
        variant: "primary",
        color: "positive",
        class: `bg-action-fill-positive-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-positive-hover 
          disabled:bg-action-fill-primary-disabled
          disabled:text-action-ink-primary-disabled
          active:bg-action-fill-positive-activated
          `,
      },
      {
        variant: "primary",
        color: "negative",
        class: `bg-action-fill-negative-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-negative-hover 
          disabled:bg-action-fill-negative-disabled
          disabled:text-action-ink-negative-disabled
          active:bg-action-fill-negative-activated
          `,
      },
      {
        variant: "primary",
        color: "notice",
        class: `bg-action-fill-notice-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-notice-hover 
          disabled:bg-action-fill-notice-disabled
          disabled:text-action-ink-notice-disabled
          active:bg-action-fill-notice-activated
          `,
      },
      {
        variant: "primary",
        color: "info",
        class: `bg-action-fill-info-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-info-hover 
          disabled:bg-action-fill-info-disabled
          disabled:text-action-ink-info-disabled
          active:bg-action-fill-info-activated
          `,
      },
      {
        variant: "primary",
        color: "neutral",
        class: `bg-action-fill-neutral-default text-action-ink-on-primary-normal 
          hover:bg-action-fill-neutral-hover 
          disabled:bg-action-fill-neutral-disabled
          disabled:text-action-ink-neutral-disabled
          active:bg-action-fill-neutral-activated
          `,
      },
      {
        variant: "primary",
        color: "white",
        class: `bg-action-fill-white-default text-action-ink-neutral-subtle
          hover:bg-action-fill-white-hover 
          disabled:bg-action-fill-white-disabled
          disabled:text-action-ink-neutral-disabled
          active:bg-action-fill-white-activated
          `,
      },
      // Secondary variant colors
      {
        variant: "secondary",
        color: "primary",
        class: `
          border-action-outline-primary-faded 
          text-action-ink-primary-normal 
          hover:border-action-outline-primary-faded-hover 
          hover:bg-action-fill-primary-faded-hover
          disabled:bg-action-outline-info-disabled
          disabled:text-action-ink-primary-disabled
          disabled:border-action-outline-primary-disabled
          active:border-action-outline-primary-faded-activated
          active:bg-action-fill-primary-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "positive",
        class: `
          border-action-outline-positive-faded 
          text-action-ink-positive-normal 
          hover:border-action-outline-positive-faded-hover 
          hover:bg-action-fill-positive-faded-hover 
          disabled:bg-action-outline-positive-disabled
          disabled:text-action-ink-positive-disabled
          disabled:border-action-outline-positive-disabled
          active:border-action-outline-positive-faded-activated
          active:bg-action-fill-positive-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "negative",
        class: `
          border-action-outline-negative-faded 
          text-action-ink-negative-normal 
          hover:border-action-outline-negative-faded-hover 
          hover:bg-action-fill-negative-faded-hover 
          disabled:bg-action-outline-negative-disabled
          disabled:text-action-ink-negative-disabled
          disabled:border-action-outline-negative-disabled
          active:border-action-outline-negative-faded-activated
          active:bg-action-fill-negative-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "notice",
        class: `
          border-action-outline-notice-faded 
          text-action-ink-notice-normal 
          hover:border-action-outline-notice-faded-hover 
          hover:bg-action-fill-notice-faded-hover 
          disabled:bg-action-outline-notice-disabled
          disabled:text-action-ink-notice-disabled
          disabled:border-action-outline-notice-disabled
          active:border-action-outline-notice-faded-activated
          active:bg-action-fill-notice-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "info",
        class: `border-action-outline-info-faded 
          text-action-ink-info-normal 
          hover:border-action-outline-info-faded-hover 
          hover:bg-action-fill-info-faded-hover 
          disabled:bg-action-outline-info-disabled
          disabled:text-action-ink-info-disabled
          disabled:border-action-outline-info-disabled
          active:border-action-outline-info-faded-activated
          active:bg-action-fill-info-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "neutral",
        class: `border-action-outline-neutral-faded 
          text-action-ink-neutral-normal 
          hover:bg-action-outline-neutral-faded-hover 
          hover:bg-action-fill-neutral-faded-hover 
          disabled:text-action-ink-neutral-disabled
          disabled:border-action-outline-neutral-disabled
          active:border-action-outline-neutral-faded-activated
          active:bg-action-fill-neutral-faded-activated
          `,
      },
      {
        variant: "secondary",
        color: "white",
        class: `border-action-outline-white-faded 
          text-action-ink-on-primary-subtle
          hover:border-action-outline-white-faded-hover 
          hover:bg-action-fill-white-faded-hover
          disabled:text-surface-ink-white-disabled
          disabled:border-action-outline-white-disabled
          active:border-action-outline-white-faded-activated
          active:bg-action-fill-white-faded-activated
          `,
      },
      // Tertiary variant colors
      {
        variant: "tertiary",
        color: "primary",
        class: `text-action-ink-primary-normal 
          hover:bg-action-fill-primary-faded 
          disabled:text-action-ink-on-primary-muted
          active:bg-action-fill-primary-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "positive",
        class: `text-action-ink-positive-normal 
          hover:bg-action-fill-positive-faded 
          disabled:text-action-ink-on-positive-muted
          active:bg-action-fill-positive-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "negative",
        class: `text-action-ink-negative-normal 
          hover:bg-action-fill-negative-faded 
          disabled:text-action-ink-on-negative-muted
          active:bg-action-fill-negative-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "notice",
        class: `text-action-ink-notice-normal 
          hover:bg-action-fill-notice-faded 
          disabled:text-action-ink-on-notice-muted
          active:bg-action-fill-notice-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "info",
        class: `text-action-ink-info-normal 
          hover:bg-action-fill-info-faded 
          disabled:text-action-ink-on-notice-muted
          active:bg-action-fill-info-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "neutral",
        class: `text-action-ink-neutral-normal 
          hover:bg-action-fill-neutral-faded 
          disabled:text-action-ink-on-notice-muted
          active:bg-action-fill-neutral-faded-activated
          `,
      },
      {
        variant: "tertiary",
        color: "white",
        class: `text-action-ink-white-subtle
          hover:bg-action-white-faded-hover
          disabled:text-action-ink-white-disabled
          disabled:bg-action-fill-white-disabled
          active:bg-action-white-faded-activated
          `,
      },
      // Icon only sizing
      {
        isIconOnly: true,
        size: "xsmall",
        class: "h-7 w-7",
      },
      {
        isIconOnly: true,
        size: "small",
        class: "h-8 w-8",
      },
      {
        isIconOnly: true,
        size: "medium",
        class: "h-9 w-9",
      },
      {
        isIconOnly: true,
        size: "large",
        class: "h-10 w-10",
      },
    ],
    defaultVariants: {
      variant: "primary",
      color: "primary",
      size: "medium",
      isIconOnly: false,
      isLoading: false,
      isFullWidth: false,
    },
  }
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  variant?: "primary" | "secondary" | "tertiary";
  color?:
    | "primary"
    | "positive"
    | "negative"
    | "notice"
    | "info"
    | "neutral"
    | "white";
  size?: "xsmall" | "small" | "medium" | "large";
  isIconOnly?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  asChild?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      color = "primary",
      size,
      isIconOnly,
      isLoading,
      asChild = false,
      leadingIcon,
      trailingIcon,
      isFullWidth = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    const isDisabled = disabled || isLoading || false;

    const getLoaderColor = () => {
      if (color === "white") {
        return "var(--color-surface-ink-neutral-normal)";
      }
      return `var(--color-action-ink-${color}-normal)`;
    };

    const buttonContent = (
      <>
        {isLoading && !isIconOnly && (
          <PulseLoader color={getLoaderColor()} size={10} />
        )}
        {isLoading && isIconOnly && (
          <ClipLoader color={getLoaderColor()} size={20} />
        )}
        {!isLoading && leadingIcon && (
          <span className="mr-2">{leadingIcon}</span>
        )}
        {!isIconOnly && !isLoading && children}
        {isIconOnly && !isLoading && children}
        {!isLoading && trailingIcon && <span>{trailingIcon}</span>}
      </>
    );

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            color,
            size,
            isIconOnly,
            isLoading,
            isDisabled,
            isFullWidth,
          }),
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {buttonContent}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
